import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Route, Switch } from "react-router-dom";
import { Layout, BackTop, ConfigProvider } from "antd";
import { createIntl, createIntlCache, RawIntlProvider } from "react-intl";

import csCZ from "../../messages/csCZ";
import enGB from "../../messages/enGB";
import plPL from "../../messages/plPL";

import "moment/locale/cs";
import "moment/locale/pl";
import "moment/locale/en-gb";
import antCSCZ from "antd/es/locale/cs_CZ";

import Home from "../home";
import Login from "../login";
import {
  isLoginUser,
  isDocumentUser,
  isDocsAdminUser,
  notDocumentUser,
} from "../../modules/session";
import SignIn from "../signIn";
import NoRoles from "../noRoles";
import NoMatch from "../noMatch";
import Address from "../address";
import colors from "../../colors";
import UserList from "../userList";
import Contracts from "../contracts";
import Calculator from "../calculator";
import Consignment from "../consignment";
import AddressList from "../addressList";
import InvoiceList from "../invoiceList";
import NewDocument from "../newDocument";
import DocumentList from "../documentList";
import Notification from "../notification";
import TemplateList from "../templateList";
import AddressImport from "../addressImport";
import ConsignmentEdit from "../consignmentEdit";
import ConsignmentList from "../consignmentList";
import Footer from "../../components/app/footer";
import NavBar from "../../components/app/navBar";
import OneTimeLoginList from "../oneTimeLoginList";
import NotificationList from "../notificationList";
import SideBar from "../../components/app/sideBar";
import ConsignmentDetail from "../consignmentDetail";
import ConsignmentImport from "../consignmentImport";
import UserChangePassword from "../userChangePassword";
import ConsignmentListSent from "../consignmentListSent";
import AlertMessage from "../../components/app/alertMessage";
import PrivateRoute from "../../components/app/privateRoute";
import { initAuth } from "../../modules/redux/session/action";
import ConsignmentListTracking from "../consignmentListTracking";
import CustomIcon from "../../components/app/generic/customIcon";
import EnvironmentBackground from "../../components/app/environmentBackground";

import "./index.css";

const { Content } = Layout;

const initialLocale = "cs-CZ";
const defaultLanguageSetting = { locale: initialLocale, antLocale: antCSCZ };

const cache = createIntlCache();
export let intl = createIntl({ locale: initialLocale, messages: csCZ }, cache);

const getLocale = (locale) =>
  locale === "en-GB" ? enGB : locale === "pl-PL" ? plPL : csCZ;

function App() {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(initAuth());
  }, [dispatch]);

  const nonce = useMemo(() => {
    const node = document.getElementById("nonce-value");
    return node ? node.getAttribute("value") : "";
  }, []);

  const [languageSetting, setLanguageSetting] = useState(
    defaultLanguageSetting,
  );

  const handleLanguageSet = useCallback((newSetting) => {
    setLanguageSetting(newSetting);
    intl = createIntl(
      {
        locale: newSetting.locale,
        messages: getLocale(newSetting.locale),
      },
      cache,
    );
    document.documentElement.lang = newSetting.locale;
  }, []);

  const [countOfDocs, setCountOfDocs] = useState();

  const isOtherThanDocuments = (user) => notDocumentUser(user?.role);
  const handleContractCheck = (_, client) => client && client.contractSetting;
  const handleNotificationCheck = (user) => user && user.notifications;
  const handleOneTimeLoginCheck = (user) => isLoginUser(user?.role);
  const handleDocsAdminCheck = (user) => isDocsAdminUser(user?.role);
  const handleDocsUserCheck = (user) => isDocumentUser(user?.role);

  return (
    <RawIntlProvider value={intl}>
      <ConfigProvider csp={{ nonce }} locale={languageSetting.antLocale}>
        <Layout className="app">
          <NavBar
            onSetCountOfDocs={setCountOfDocs}
            onLanguageSetting={handleLanguageSet}
          />
          <Layout>
            <SideBar countOfDocs={countOfDocs} />
            <Content className="app-content">
              <AlertMessage intl={intl} />
              <EnvironmentBackground />
              <Switch>
                <Route path="/login" component={Login} />
                <Route path="/sign-in" component={SignIn} />
                <Route path="/no-roles" component={NoRoles} />
                <PrivateRoute exact path="/" component={Home} />
                <PrivateRoute
                  exact
                  path="/new-consignment"
                  component={Consignment}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/new-template"
                  component={Consignment}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/consignments"
                  component={ConsignmentListSent}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/track-consignments"
                  component={ConsignmentListTracking}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/work-consignments"
                  component={ConsignmentList}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/consignment/:consignmentId/add"
                  component={ConsignmentEdit}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/consignment/:consignmentId/edit"
                  component={ConsignmentEdit}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/consignment/:consignmentId/detail"
                  component={ConsignmentDetail}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/consignment-templates"
                  component={TemplateList}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/consignment-import"
                  component={ConsignmentImport}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/new-customer"
                  component={Address}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/customer/:addressId/add"
                  component={Address}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/customer/:addressId/edit"
                  component={Address}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/customer-import"
                  component={AddressImport}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/calculator"
                  component={Calculator}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/customers"
                  component={AddressList}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/invoices"
                  component={InvoiceList}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/users"
                  component={UserList}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/change-password"
                  component={UserChangePassword}
                  checkAccessFunction={isOtherThanDocuments}
                />
                <PrivateRoute
                  exact
                  path="/notifications"
                  component={NotificationList}
                  checkAccessFunction={handleNotificationCheck}
                />
                <PrivateRoute
                  exact
                  path="/new-notification"
                  component={Notification}
                  checkAccessFunction={handleNotificationCheck}
                />
                <PrivateRoute
                  exact
                  path="/notification/:notificationId/edit"
                  component={Notification}
                  checkAccessFunction={handleNotificationCheck}
                />
                <PrivateRoute
                  exact
                  path="/contracts"
                  component={Contracts}
                  checkAccessFunction={handleContractCheck}
                />
                <PrivateRoute
                  exact
                  path="/one-time-login"
                  component={OneTimeLoginList}
                  checkAccessFunction={handleOneTimeLoginCheck}
                />
                <PrivateRoute
                  exact
                  path="/documents"
                  component={DocumentList}
                  checkAccessFunction={handleDocsUserCheck}
                />
                <PrivateRoute
                  exact
                  path="/new-document"
                  component={NewDocument}
                  checkAccessFunction={handleDocsAdminCheck}
                />
                <PrivateRoute
                  exact
                  path="/document/:documentId/edit"
                  component={NewDocument}
                  checkAccessFunction={handleDocsAdminCheck}
                />
                <PrivateRoute path="*" component={NoMatch} />
              </Switch>
            </Content>
          </Layout>
          <Footer />
          <BackTop>
            <CustomIcon type="up-circle" color={colors.RED} size="44px" />
          </BackTop>
        </Layout>
      </ConfigProvider>
    </RawIntlProvider>
  );
}

export default App;
